// ─────────────────────────────────────────────────────────────────────
// <ChipFilterRow /> — design vision spec
//
// Horizontally scrollable chip row.  Active chip = white bg / dark text.
// Sticky on mobile under TopNav.
// ─────────────────────────────────────────────────────────────────────

import { useRef, useEffect, useState } from 'react';

export default function ChipFilterRow({
  chips = [],
  active,
  onChange,
  sticky = false,
  className = '',
}) {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  // Detect overflow to show fade gradients
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setShowLeft(el.scrollLeft > 4);
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [chips]);

  return (
    <div
      className={[
        'relative w-full',
        sticky ? 'sticky top-[68px] z-20 lg:static lg:z-auto' : '',
        'border-b border-line',
        className,
      ].join(' ')}
      style={sticky ? { background: 'rgba(10,10,11,0.92)', backdropFilter: 'blur(20px)' } : undefined}
    >
      {showLeft && (
        <div
          className="absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none"
          style={{
            background: 'linear-gradient(to right, var(--snt-bg), transparent)',
          }}
        />
      )}
      {showRight && (
        <div
          className="absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none"
          style={{
            background: 'linear-gradient(to left, var(--snt-bg), transparent)',
          }}
        />
      )}

      <div
        ref={scrollRef}
        className="flex gap-2.5 overflow-x-auto px-9 py-7 snt-no-scrollbar"
        style={{ scrollbarWidth: 'none' }}
      >
        {chips.map((chip) => {
          const isActive =
            active === chip.value || (Array.isArray(active) && active.includes(chip.value));
          return (
            <button
              key={chip.value}
              type="button"
              onClick={() => onChange?.(chip.value)}
              className={`snt-chip flex-shrink-0 ${isActive ? 'active' : ''}`}
              aria-pressed={isActive}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
      <style>{`.snt-no-scrollbar::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
}
